import { Injectable } from "@angular/core";

@Injectable()
export class LocalStorageService {
    constructor() {}

    /**
     * Will try to set an item to the local storage.
     *
     * If a quota exceeded exception is thrown, this function will fail silently, unless the mandatory parameter is set to true.
     */
    public static setItem(key: string, value: any, mandatory = false) {
        try {
            localStorage.setItem(key, value);
        } catch (e) {
            if (e instanceof DOMException) {
                console.error(`Error setting '${key}' in localStorage:`, e.message);
                if (mandatory) {
                    throw e;
                }
            } else {
                console.error("Unexpected error:", e);
                throw e;
            }
        }
    }

    /**
     * Will fetch from the local storage the given key, parse it to an object and set the given value using the provided path.
     *
     * Will overwrite any non-object item already present in the local storage.
     *
     * If a quota exceeded exception is thrown, this function will fail silently, unless the mandatory parameter is set to true.
     */
    public static setObject(key: string, path: string[], value, mandatory = false) {
        if (!path || path.length == 0) {
            this.setItem(key, value, mandatory);
        } else {
            const item = this.tryParse(localStorage.getItem(key));
            this.setItem(key, JSON.stringify(this.setObjectInternal(item instanceof Object ? item : {}, path, value)), mandatory);
        }
    }

    private static setObjectInternal(obj: object, path: string[], value) {
        if (path.length == 0) {
            return value;
        } else {
            obj[path[0]] ??= {};
            obj[path[0]] = this.setObjectInternal(obj[path[0]], path.slice(1), value);
            return obj;
        }
    }

    /**
     * Will fetch from the local storage the given key, and parse it to an object. Then, will
     * set the given value to the given object path.
     *
     * Will overwrite any non-object item already present in the local storage.
     *
     * If a quota exceeded exception is thrown, this function will fail silently, unless the mandatory parameter is set to true.
     */
    public static removeObject(key: string, path: string[], mandatory = false) {
        const item = this.tryParse(localStorage.getItem(key));
        if (!item || !(item instanceof Object) || !path || path.length == 0) {
            localStorage.removeItem(key);
        } else {
            const obj = this.removeObjectInternal(item instanceof Object ? item : {}, path);
            if (Object.keys(obj).length == 0) {
                localStorage.removeItem(key);
            } else {
                this.setItem(key, JSON.stringify(obj), mandatory);
            }
        }
    }

    private static removeObjectInternal(obj: object, path: string[]): any {
        if (path.length == 1) {
            delete obj[path[0]];
            return obj;
        } else {
            if (obj[path[0]]) {
                obj[path[0]] = this.removeObjectInternal(obj[path[0]], path.slice(1));
                if (Object.keys(obj[path[0]]).length === 0) {
                    delete obj[path[0]];
                }
            }
            return obj;
        }
    }

    private static tryParse(potential) {
        let parsed;
        try {
            parsed = JSON.parse(potential);
        } catch (e) {
            parsed = null;
        }
        return parsed;
    }
}
